<template>
  <div class="cart-page">
    <h1>購物車</h1>

    <!-- 如果購物車是空的，就提示一下 -->
    <div v-if="cartCount === 0" class="empty-cart">
      <p>你的購物車沒有任何商品。</p>
      <router-link to="/products" class="btn-continue">繼續逛逛</router-link>
    </div>
    
    <!-- 否則顯示購物車清單 -->
    <div v-else class="cart-container">
      <!-- 購物車列表區 -->
      <div class="cart-list">
        <!-- 單個商品 (cart-item) -->
        <div
          v-for="(item) in cartItems"
          :key="item.product_id + '_' + (item.variant_id || '')"
          class="cart-item"
        >
          <!-- 商品圖片 -->
          <img
            v-if="item.image_url"
            :src="item.image_url"
            alt="商品圖"
            class="item-image"
          />
          <!-- 文字資訊區 -->
          <div class="item-info">
            <!-- 若有 variant_name，就一起顯示 (可自行調整) -->
            <h2 class="item-title">
              {{ item.title }}
              <span v-if="item.variant_name">- {{ item.variant_name }}</span>
            </h2>
            <!-- 單價 (含千分位) -->
            <p class="item-subtitle">NT${{ formatPrice(item.discount_price) }}</p>

            <!-- 數量調整：改成「單一外框」風格 -->
            <div class="qty-control">
              <button @click="decreaseQty(item)">-</button>
              <input
                type="number"
                inputmode="numeric"
                v-model.number="item.quantity"
                min="1"
                max="99"
                @change="updateQty(item)"
              />
              <button @click="increaseQty(item)">+</button>
            </div>

            <!-- 小計 (含千分位) -->
            <p class="item-subtotal">
              小計：NT${{ formatPrice(item.discount_price * item.quantity) }}
            </p>
          </div>

          <!-- 刪除：右上角 X -->
          <button
            class="delete-button"
            @click="removeItem(item)"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- 小計 / 折扣券 / 總計 & 前往結帳 -->
      <div class="cart-summary">
        <p class="summary-line">
          <span>商品小計：</span>
          <span>NT${{ formatPrice(cartSubtotal) }}</span>
        </p>

        <!-- 滿額贈區塊 -->
        <div v-if="allGifts.length > 0" class="gift-section">
          <p class="gift-title">🎁 滿額贈</p>
          <div v-for="g in earnedGifts" :key="g.id" class="gift-item gift-item--earned">
            🎁 已獲贈品：{{ g.gift_name }} × {{ g.gift_quantity }}
          </div>
          <div v-if="nextGift" class="gift-next">
            再消費 NT${{ (nextGift.min_amount - cartSubtotal).toLocaleString() }} 即贈 {{ nextGift.gift_name }}
          </div>
        </div>

        <!-- 折扣券區塊 -->
        <div class="coupon-section">
          <p class="coupon-title">🏷️ 折扣券</p>
          <!-- 無可用折扣券 -->
          <div v-if="availableCoupons.length === 0" class="coupon-empty">
            <span v-if="allCoupons.length > 0">
              尚未達到折扣門檻（最低 NT${{ allCoupons[0].min_amount.toLocaleString() }}）
            </span>
            <span v-else>目前無可用折扣券</span>
          </div>
          <!-- 有可用折扣券，讓用戶選擇 -->
          <div v-else class="coupon-list">
            <div
              v-for="coupon in availableCoupons"
              :key="coupon.id"
              class="coupon-card"
              :class="{ 'coupon-card--selected': selectedCouponId === coupon.id }"
              @click="toggleCoupon(coupon)"
            >
              <div class="coupon-card__info">
                <span class="coupon-tag">滿 NT${{ coupon.min_amount.toLocaleString() }} 享 {{ Number(coupon.discount_rate) }} 折</span>
                <span class="coupon-tag-after">券後 <strong>NT${{ Math.round(cartSubtotal * Number(coupon.discount_rate) / 10).toLocaleString() }}</strong>&ensp;省 NT${{ (cartSubtotal - Math.round(cartSubtotal * Number(coupon.discount_rate) / 10)).toLocaleString() }}</span>
                <span v-if="coupon.days_left != null" class="coupon-tag-expire">⏳ 倒數 {{ coupon.days_left }} 天</span>
              </div>
              <span class="coupon-card__check" v-if="selectedCouponId === coupon.id">✓ 已套用</span>
              <span class="coupon-card__use" v-else>點擊套用</span>
            </div>
          </div>
          <!-- 已套用折扣 -->
          <p v-if="selectedCouponId" class="coupon-discount-line">
            <span>折扣：</span>
            <span class="coupon-discount-amt">－ NT${{ formatPrice(selectedCouponDiscount) }}</span>
          </p>
        </div>

        <p class="summary-line total">
          <span>總計：</span>
          <span>NT${{ formatPrice(cartTotal) }}</span>
        </p>

        <!-- 前往結帳按鈕 -->
        <button class="btn-checkout" @click="goToCheckout">
          前往結帳
        </button>
        <!-- 繼續逛逛 -->
        <router-link to="/products" class="btn-continue">繼續逛逛</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import axios from 'axios'

export default {
  name: 'CartPage',
  data() {
    return {
      allCoupons: [],
      selectedCouponId: null,
      allGifts: [],
    }
  },
  computed: {
    ...mapState(['cartItems']),
    ...mapGetters(['cartCount']),

    // 商品小計
    cartSubtotal() {
      return this.cartItems.reduce((sum, item) => {
        return sum + (item.discount_price * item.quantity)
      }, 0)
    },
    // 達到門檻的可用折扣券（依折數升序：折數越小越划算，排前面）
    availableCoupons() {
      return this.allCoupons
        .filter(c => this.cartSubtotal >= c.min_amount)
        .sort((a, b) => Number(a.discount_rate) - Number(b.discount_rate))
    },
    // 已選折扣券的折扣金額（原價 - 折後價）
    selectedCouponDiscount() {
      if (!this.selectedCouponId) return 0
      const c = this.allCoupons.find(c => c.id === this.selectedCouponId)
      if (!c) return 0
      return this.cartSubtotal - Math.round(this.cartSubtotal * Number(c.discount_rate) / 10)
    },
    cartTotal() {
      return Math.max(0, this.cartSubtotal - this.selectedCouponDiscount)
    },
    earnedGifts() {
      return this.allGifts.filter(g => this.cartSubtotal >= g.min_amount)
    },
    nextGift() {
      const unearned = this.allGifts.filter(g => this.cartSubtotal < g.min_amount)
      if (!unearned.length) return null
      return unearned.sort((a, b) => a.min_amount - b.min_amount)[0]
    },
  },
  async created() {
    await Promise.all([this.fetchCoupons(), this.fetchGifts()])
    // 恢復上次選的折扣券
    const saved = localStorage.getItem('selectedCoupon')
    if (saved) {
      try {
        const obj = JSON.parse(saved)
        this.selectedCouponId = obj.id || null
      } catch (e) { /* ignore */ }
    }
    // 若無已選折扣券，自動套用最優惠的可用券
    if (!this.selectedCouponId) {
      const available = this.allCoupons.filter(c => this.cartSubtotal >= c.min_amount)
      if (available.length > 0) {
        // 選折扣率最低（最優惠）的券
        const best = available.reduce((a, b) => Number(a.discount_rate) < Number(b.discount_rate) ? a : b)
        this.selectedCouponId = best.id
        localStorage.setItem('selectedCoupon', JSON.stringify(best))
      }
    }
  },
  watch: {
    // 若購物車金額變動，確認已選折扣券是否仍符合門檻
    cartSubtotal(val) {
      if (this.selectedCouponId) {
        const c = this.allCoupons.find(c => c.id === this.selectedCouponId)
        if (c && val < c.min_amount) {
          this.selectedCouponId = null
          localStorage.removeItem('selectedCoupon')
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'removeProductFromCart',
      'addProductToCart'
    ]),
    ...mapMutations(['setCartItems']),

    async fetchGifts() {
      try {
        const res = await axios.get('/api/load/threshold-gifts')
        if (res.data.success) this.allGifts = res.data.gifts
      } catch (e) {
        console.error('fetch gifts error:', e)
      }
    },
    async fetchCoupons() {
      try {
        const res = await axios.get('/api/order/coupons')
        if (res.data.success) this.allCoupons = res.data.coupons
      } catch (e) {
        console.error('fetch coupons error:', e)
      }
    },

    toggleCoupon(coupon) {
      if (this.selectedCouponId === coupon.id) {
        // 再點一次取消
        this.selectedCouponId = null
        localStorage.removeItem('selectedCoupon')
      } else {
        this.selectedCouponId = coupon.id
        localStorage.setItem('selectedCoupon', JSON.stringify(coupon))
      }
    },

    formatPrice(num) {
      if (!num) return 0
      return num.toLocaleString('en-US')
    },

    removeItem(item) {
      const productId = item.product_id
      const variantId = item.variant_id || ''
      this.removeProductFromCart({ productId, variantId })
    },

    increaseQty(item) {
      if (item.quantity < 99) {
        item.quantity++
        this.setCartItems([...this.cartItems])
      }
    },
    decreaseQty(item) {
      if (item.quantity > 1) {
        item.quantity--
        this.setCartItems([...this.cartItems])
      }
    },
    updateQty(item) {
      if (item.quantity < 1) item.quantity = 1
      if (item.quantity > 99) item.quantity = 99
      this.setCartItems([...this.cartItems])
    },
    goToCheckout() {
      localStorage.setItem('earnedGifts', JSON.stringify(this.earnedGifts))
      this.$router.push('/checkout')
    }
  }
}
</script>

<style scoped>
/* 整體容器 */
.cart-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background: #f9f9f9;
  
  /* 整體字體大一些 */
  font-size: 1.1rem; /* 原本可能是 1rem，調大到 1.1rem 或 1.2rem */
  line-height: 1.5;  /* 增加行高讓閱讀更舒服 */
}
.cart-page h1 {
  margin-bottom: 30px;
  font-size: 2.2rem; /* 標題再大些 */
  text-align: center;
  color: #444;
  letter-spacing: 2px; 
}

/* 外層兩欄 */
.cart-container {
  display: flex;
  gap: 20px;
}

.cart-list {
  flex: 3;
  display: flex;
  flex-direction: column;
  gap: 20px; 
}

/* 單個商品 (Card) */
.cart-item {
  min-width: 500px;   
  position: relative;
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

@media (max-width: 768px) {
  .cart-item {
    min-width: auto;
    flex-direction: column; 
    align-items: flex-start;
  }
  .cart-page {
    margin: 0;          
    padding: 20px 10px;
    font-size: 1rem; /* 手機可稍微縮小 */
  }
  .cart-container {
    flex-wrap: wrap;
  }
  .cart-summary {
    width: 100%;
    max-width: 100%;
  }
  
}

/* 刪除按鈕 (X) */
.delete-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.3rem;
  color: #999;
  cursor: pointer;
  transition: color 0.2s;
}
.delete-button:hover {
  color: #666;
}

/* 商品圖片 */
.item-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.item-title {
  max-height: 2.4em; 
  overflow: hidden;
  font-size: 1.3rem; /* 商品標題更大 */
  font-weight: bold;
  color: #333;
}
.item-subtitle {
  font-size: 1rem; /* 單價也略大 */
  color: #777;
}

/* 數量控制：單一外框 */
.qty-control {
  display: inline-flex;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  height: 40px;
}
.qty-control button {
  width: 44px; /* 稍加寬 */
  background: #f5f5f5;
  border: none;
  font-size: 1.6rem;
  color: #444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.qty-control input {
  width: 58px; /* 稍加寬 */
  border: none;
  text-align: center;
  font-size: 1.1rem;
  outline: none;
}
.qty-control button:hover {
  background: #eaeaea;
}

/* 小計 */
.item-subtotal {
  margin-top: 4px;
  font-weight: bold;
  color: #666;
  font-size: 1.05rem; 
}

/* 右側總計卡片 */
.cart-summary {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  padding: 24px; /* 微調 */
  min-width: 280px;
  height: fit-content;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.summary-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #555;
  font-size: 1.1rem;
}
.summary-line.total {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
  margin-top: 10px;
}

/* 按鈕：前往結帳 */
.btn-checkout {
  display: block;
  width: 100%;
  background: linear-gradient(45deg, #ff6600, #ff7a00);
  border: none;
  border-radius: 4px;
  text-align: center;
  padding: 14px 0;
  font-size: 1.1rem; /* 稍大 */
  color: #fff;
  margin-top: 20px;
  cursor: pointer;
  transition: transform 0.2s;
}
.btn-checkout:hover {
  transform: translateY(-2px);
}

/* 繼續逛逛 */
.btn-continue {
  display: inline-block;
  margin-top: 10px;
  background: none;
  color: #555;
  border: 2px solid #ccc;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 1rem;
  text-decoration: none;
}
.btn-continue:hover {
  border-color: #999;
  color: #333;
}

/* 空購物車樣式 */
.empty-cart {
  text-align: center;
  margin: 60px 0;
}
.empty-cart p {
  font-size: 1.2rem; 
  margin-bottom: 15px;
}

/* ── 滿額贈區塊 ── */
.gift-section {
  border-top: 1px dashed #e0e0e0;
  padding: 10px 0;
  margin: 8px 0;
}
.gift-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #444;
  margin: 0 0 6px;
}
.gift-item--earned {
  font-size: 0.88rem;
  color: #27ae60;
  font-weight: 600;
  padding: 4px 0;
}
.gift-next {
  font-size: 0.83rem;
  color: #e67e22;
  margin-top: 4px;
}

/* ── 折扣券區塊 ── */
.coupon-section {
  border-top: 1px dashed #e0e0e0;
  border-bottom: 1px dashed #e0e0e0;
  padding: 12px 0;
  margin: 10px 0;
}
.coupon-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #444;
  margin: 0 0 8px;
}
.coupon-empty {
  font-size: 0.83rem;
  color: #999;
}
.coupon-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.coupon-card__info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
}
.coupon-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  background: #fafafa;
  transition: border-color .2s, background .2s;
}
.coupon-card:hover {
  border-color: #ff6600;
  background: #fff8f3;
}
.coupon-card--selected {
  border-color: #ff6600;
  background: #fff3e8;
}
.coupon-tag {
  font-size: 0.9rem;
  font-weight: 600;
  color: #cc4400;
}
.coupon-tag-sub {
  font-size: 0.78rem;
  color: #e67e22;
  margin-top: 2px;
}
.coupon-tag-after {
  font-size: 0.82rem;
  color: #c0392b;
}
.coupon-tag-after strong {
  font-size: 1rem;
  font-weight: 700;
}
.coupon-tag-expire {
  font-size: 0.75rem;
  color: #c0392b;
  margin-top: 2px;
  font-weight: 600;
}
.coupon-card__check {
  font-size: 0.82rem;
  color: #27ae60;
  font-weight: 700;
}
.coupon-card__use {
  font-size: 0.78rem;
  color: #aaa;
}
.coupon-discount-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
  color: #c0392b;
  margin: 8px 0 0;
}
.coupon-discount-amt {
  font-weight: 700;
}

/* 移除 input[type="number"] 預設微調按鈕 (Chrome、Safari、Edge) */
.qty-control input[type="number"]::-webkit-outer-spin-button,
.qty-control input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* Firefox */
.qty-control input[type="number"] {
  -moz-appearance: textfield;
}


</style>
